import { isRefreshAvailable, LOCATION_SUMMARY_REFRESH_COOLDOWN_MS } from "api-types/locations/locationSummaryRefresh";
import { useEffect, useState } from "react";
import Button from "ui/buttons/Button";
import useAppContext from "ui/hooks/useAppContext";
import IconRefresh from "ui/icons/IconRefresh";
import { wrapWithTooltip } from "ui/popovers/Tooltip";
import styles from "./LocationSummaryView.module.scss";

interface SummaryRefreshControlProps {
  aggregatedAt: string;
  onRefresh: () => Promise<void>;
  isRefreshing: boolean;
}

function getCooldownTooltip(aggregatedAt: string, now: Date): string | undefined {
  if (isRefreshAvailable(aggregatedAt, now)) {
    return undefined;
  }

  const availableAt = Date.parse(aggregatedAt) + LOCATION_SUMMARY_REFRESH_COOLDOWN_MS;
  const remainingMinutes = Math.ceil((availableAt - now.getTime()) / 60_000);
  return `Refresh available in ~${remainingMinutes} min`;
}

export default function SummaryRefreshControl({ aggregatedAt, onRefresh, isRefreshing }: SummaryRefreshControlProps) {
  const { setUserNotification } = useAppContext();
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const intervalId = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(intervalId);
  }, []);

  const refreshAvailable = isRefreshAvailable(aggregatedAt, now);
  const tooltip = getCooldownTooltip(aggregatedAt, now);
  const parsedAggregatedAt = Date.parse(aggregatedAt);
  const updatedAt = Number.isNaN(parsedAggregatedAt)
    ? undefined
    : new Date(parsedAggregatedAt).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
  const button = (
    <Button
      aria-label="Refresh location summary"
      btnSize="small"
      btnStyle="link"
      disabled={!refreshAvailable || isRefreshing}
      isPending={isRefreshing}
      pendingChildren="Refreshing"
      onClick={() => {
        void onRefresh().catch(() => {
          setUserNotification({
            messageType: "error",
            message: "Failed to refresh the location summary. Please try again.",
          });
        });
      }}
    >
      <IconRefresh />
      Refresh
    </Button>
  );

  return (
    <div className={styles.refreshControl}>
      {updatedAt !== undefined && <span className={styles.aggregatedAt}>Updated {updatedAt}</span>}
      {wrapWithTooltip(
        <span className={styles.refreshButtonWrapper} tabIndex={tooltip ? 0 : undefined}>
          {button}
        </span>,
        tooltip,
      )}
    </div>
  );
}
